import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import Icon from './Icon';
import { supabase } from '../data/supabase';
import { useAuth } from '../data/auth';
import { useToast } from './Toast';

/* ═══════════════════════════════════════════════════════
   COREFLOW NOTIFICATION BELL
   - Unread count badge on bell icon
   - Dropdown panel with notification list
   - Supabase Realtime subscription for live updates
   - Mark read, mark all read, clear actions
   - Category filtering + grouping
   - Click-to-navigate via onAction callback
   ═══════════════════════════════════════════════════════ */

// Notification chime — uses Web Audio API, no external files
let _audioCtx = null;
function playChime() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const now = ctx.currentTime;

    // Two-tone chime: pleasant ascending notes
    [520, 680].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.4);
    });
  } catch (e) { /* Audio not available */ }
}

const CATEGORY_META = {
  deal_change: { icon: 'briefcase', color: T.blue, label: 'Deals' },
  task:        { icon: 'check',     color: "#D4763A",  label: 'Tasks' },
  project:     { icon: 'folder',    color: T.blue, label: 'Projects' },
  email:       { icon: 'mail',      color: "#6B5B95",  label: 'Email' },
  sms:         { icon: 'chat',      color: "#2E8B57",  label: 'SMS' },
  call:        { icon: 'phone',     color: "#3D8B8B",  label: 'Calls' },
  workflow:    { icon: 'workflow',   color: T.blue, label: 'Workflows' },
  mention:     { icon: 'chat',      color: "#6B5B95",  label: 'Chat' },
  calendar:    { icon: 'calendar',  color: "#D4763A",  label: 'Calendar' },
  campaign:    { icon: 'send',      color: "#3D8B8B",  label: 'Campaigns' },
  system:      { icon: 'info',      color: T.tx3,  label: 'System' },
};

const SEVERITY_COLORS = {
  info: T.blue,
  warning: "#D4763A",
  urgent: "#E53935",
};

function timeAgo(dateStr) {
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  if (s < 604800) return Math.floor(s / 86400) + 'd ago';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function NotificationBell({ onNavigate }) {
  const auth = useAuth();
  const toast = useToast();
  const userId = auth.currentUser?._dbId;
  const orgId = auth.currentUser?._orgId;

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or category
  const [animateBell, setAnimateBell] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 60, right: 16 });
  const isMobileNotif = typeof window !== 'undefined' && window.innerWidth < 768;

  // Compute panel position when opening
  useEffect(() => {
    if (open && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    }
  }, [open]);

  // ── Fetch notifications ──
  const fetchNotifications = useCallback(async () => {
    if (!userId || !orgId) return;
    try {
      const token = (await supabase.auth.getSession())?.data?.session?.access_token;
      if (!token) return;

      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (filter !== 'all') params.set('category', filter);

      const res = await fetch(`/api/notifications?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      console.warn('[CoreFlow] Notification fetch:', e.message);
    } finally {
      setLoading(false);
    }
  }, [userId, orgId, filter]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // ── Supabase Realtime subscription ──
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('notifications-' + userId)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        const n = payload.new;
        setNotifications(prev => [n, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        // Animate bell
        setAnimateBell(true);
        setTimeout(() => setAnimateBell(false), 600);
        // Play chime
        playChime();
        // Browser notification if tab not focused
        if (document.hidden && Notification.permission === 'granted') {
          new Notification(n.title, { body: n.body || '', icon: '/favicon.ico', tag: n.id });
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        setNotifications(prev => prev.map(n => n.id === payload.new.id ? { ...n, ...payload.new } : n));
        // Recalculate unread
        setNotifications(prev => {
          const unread = prev.filter(n => !n.read).length;
          setUnreadCount(unread);
          return prev;
        });
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        setNotifications(prev => prev.filter(n => n.id !== payload.old.id));
        setUnreadCount(prev => Math.max(0, prev - 1));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  // ── Browser push permission — only request when user clicks in settings ──

  // ── Close on outside click ──
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Actions ──
  const getToken = async () => (await supabase.auth.getSession())?.data?.session?.access_token;

  const markRead = async (id) => {
    const token = await getToken();
    if (!token) return;
    const prevNotifications = notifications;
    const prevUnread = unreadCount;
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      const res = await fetch('/api/notifications', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_read', notification_id: id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setNotifications(prevNotifications); // rollback
      setUnreadCount(prevUnread);
      console.error("[NotificationBell] Failed to mark notification as read:", err);
      toast?.error?.("Failed to mark notification as read: " + (err.message || "unknown error"));
    }
  };

  const markAllRead = async () => {
    const token = await getToken();
    if (!token) return;
    const prevNotifications = notifications;
    const prevUnread = unreadCount;
    setNotifications(prev => prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() })));
    setUnreadCount(0);
    try {
      const res = await fetch('/api/notifications', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_all_read' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setNotifications(prevNotifications); // rollback
      setUnreadCount(prevUnread);
      console.error("[NotificationBell] Failed to mark all notifications as read:", err);
      toast?.error?.("Failed to mark all notifications as read: " + (err.message || "unknown error"));
    }
  };

  const deleteNotification = async (id) => {
    const token = await getToken();
    if (!token) return;
    const n = notifications.find(x => x.id === id);
    setNotifications(prev => prev.filter(x => x.id !== id));
    if (n && !n.read) setUnreadCount(prev => Math.max(0, prev - 1));
    fetch(`/api/notifications?notification_id=${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(console.error);
  };

  const clearAll = async () => {
    const token = await getToken();
    if (!token) return;
    setNotifications([]);
    setUnreadCount(0);
    fetch('/api/notifications?clear_all=true', {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(console.error);
  };

  const handleNotifClick = (n) => {
    if (!n.read) markRead(n.id);
    if (onNavigate) {
      // Set global target so destination page can auto-select
      window.__coreflow_notification_target = {
        type: n.action_type,
        id: n.action_id,
        meta: n.action_meta || {},
        category: n.category,
      };

      // Map category + action_type to the right page
      const routeMap = {
        sms: 'sms',
        call: 'call-logs',
        email: 'email',
        deal_change: 'deals',
        task: 'tasks',
        project: 'projects',
        workflow: 'workflows',
        campaign: 'workflows',
        mention: 'team-chat',
        calendar: 'dashboard',
        system: 'admin',
      };
      // Use category for routing (more reliable than action_type)
      const route = routeMap[n.category] || (n.action_type === 'contact' ? 'contacts' : 'dashboard');
      onNavigate(route, n.action_id, n.action_type, n.category, n.action_meta);
      setOpen(false);
    }
  };

  const requestPushPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const filtered = filter === 'all' ? notifications : notifications.filter(n => n.category === filter);

  // ── Group by date ──
  const groupByDate = (items) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    items.forEach(n => {
      const d = new Date(n.created_at).toDateString();
      const label = d === today ? 'Today' : d === yesterday ? 'Yesterday' : new Date(n.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      if (!groups[label]) groups[label] = [];
      groups[label].push(n);
    });
    return groups;
  };

  const grouped = groupByDate(filtered);

  return (
    <div style={{ position: 'relative' }}>
      {/* ── Bell Button ── */}
      <button
        ref={bellRef}
        onClick={() => { setOpen(!open); }}
        style={{
          position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
          padding: 6, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
          animation: animateBell ? 'bellShake 0.5s ease-in-out' : 'none',
        }}
      >
        <Icon name={unreadCount > 0 ? 'bell' : 'bell'} size={18} color={open ? T.blue : '#6B7280'} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            minWidth: 16, height: 16, borderRadius: 8,
            background: "#E53935", color: '#fff',
            fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', lineHeight: 1,
            boxShadow: '0 2px 8px rgba(220,38,38,0.4)',
            animation: 'badgePulse 2s ease-in-out infinite',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Animations ── */}
      <style>{`
        @keyframes bellShake {
          0%, 100% { transform: rotate(0); }
          15% { transform: rotate(12deg); }
          30% { transform: rotate(-10deg); }
          45% { transform: rotate(8deg); }
          60% { transform: rotate(-6deg); }
          75% { transform: rotate(3deg); }
        }
        @keyframes badgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        @keyframes notifSlideIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes notifItemIn {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .cf-notif-filters::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Dropdown Panel (portaled to document.body to escape overflow:hidden) ── */}
      {open && ReactDOM.createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: isMobileNotif ? 0 : panelPos.top,
            right: isMobileNotif ? 0 : panelPos.right,
            left: isMobileNotif ? 0 : 'auto',
            bottom: isMobileNotif ? 0 : 'auto',
            width: isMobileNotif ? '100%' : 460, maxHeight: isMobileNotif ? '100%' : 600,
            background: '#2C2E33',
            border: isMobileNotif ? 'none' : '1px solid rgba(217,219,221,0.12)',
            borderRadius: isMobileNotif ? 0 : 16,
            boxShadow: '0 40px 100px rgba(0,0,0,0.40)',
            zIndex: 9990,
            display: 'flex', flexDirection: 'column',
            animation: 'notifSlideIn 0.2s ease-out',
            overflow: 'hidden',
            paddingTop: isMobileNotif ? 'env(safe-area-inset-top, 0px)' : 0,
          }}
        >
          {/* Header */}
          <div style={{ padding: isMobileNotif ? '12px 14px 10px' : '16px 18px 12px', borderBottom: '1px solid rgba(217,219,221,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isMobileNotif && <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', marginRight: 4 }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9L11 14" stroke="#FAFAFA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>}
              <span style={{ fontSize: 14, fontWeight: 700, color: T.txt }}>Notifications</span>
              {unreadCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: '#D9DBDD', background: T.blue, padding: '2px 8px', borderRadius: 6 }}>{unreadCount}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {unreadCount > 0 && <button onClick={markAllRead} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: T.blue, color: '#D9DBDD', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Mark all read</button>}
              {notifications.length > 0 && <button onClick={clearAll} style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(217,219,221,0.12)', background: 'rgba(255,255,255,0.06)', color: T.tx3, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Clear all</button>}
            </div>
          </div>

          {/* Category Filters */}
          <div className="cf-notif-filters" style={{ padding: '8px 18px', display: 'flex', gap: 5, overflowX: 'auto', borderBottom: '1px solid rgba(217,219,221,0.08)', flexShrink: 0, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {[{ key: 'all', label: 'All' }, ...Object.entries(CATEGORY_META).map(([k, v]) => ({ key: k, label: v.label }))].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{ padding: '4px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', fontFamily: 'inherit', flexShrink: 0, background: filter === f.key ? T.blue : 'rgba(255,255,255,0.06)', color: filter === f.key ? '#D9DBDD' : T.tx3 }}>{f.label}</button>
            ))}
          </div>

          {/* List */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '4px 0' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: T.tx3, fontSize: 12 }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.tx3 }}>No notifications</div>
              </div>
            ) : (
              Object.entries(grouped).map(([dateLabel, items]) => (
                <div key={dateLabel}>
                  <div style={{ padding: '8px 18px 4px', fontSize: 10, fontWeight: 700, color: T.tx3, letterSpacing: '.04em', textTransform: 'uppercase' }}>{dateLabel}</div>
                  {items.map((n, idx) => {
                    const meta = CATEGORY_META[n.category] || CATEGORY_META.system;
                    return (
                      <div key={n.id} onClick={() => handleNotifClick(n)}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', cursor: 'pointer', background: 'transparent', borderLeft: n.read ? '3px solid transparent' : '3px solid ' + T.blue, borderBottom: '1px solid rgba(217,219,221,0.06)', flexShrink: 0, animation: `notifItemIn 0.2s ease-out ${idx * 0.03}s both` }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(217,219,221,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
                          <Icon name={n.icon || meta.icon} size={14} color={T.tx3} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: n.read ? 400 : 700, color: T.txt, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                          {n.body && <div style={{ fontSize: 12, color: T.tx3, marginTop: 3, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</div>}
                          <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>{timeAgo(n.created_at)}</div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                          style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(217,219,221,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: 0.4, transition: 'opacity 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.4}>
                          <Icon name="x" size={10} color={T.tx3} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 18px', borderTop: '1px solid rgba(217,219,221,0.08)', display: 'flex', justifyContent: 'center' }}>
            <button onClick={() => { onNavigate && onNavigate('admin'); setOpen(false); }}
              style={{ padding: '5px 14px', borderRadius: 8, border: '1px solid rgba(217,219,221,0.12)', background: 'rgba(255,255,255,0.06)', color: T.tx3, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '.04em', textTransform: 'uppercase' }}>
              Settings
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
